import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Search, ShoppingCart, Plus, Minus, Trash2, X, User, CreditCard, 
  Banknote, Smartphone, Building2, Pause, Printer, Save, Settings,
  ArrowLeft, Package, ScanBarcode, AlertCircle, Check, Clock,
  Receipt, BarChart3, RotateCcw, DollarSign, Monitor, ChevronDown, MoreHorizontal
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash', icon: Banknote, color: 'emerald' },
  { value: 'UPI', label: 'UPI', icon: Smartphone, color: 'purple' },
  { value: 'CARD', label: 'Card', icon: CreditCard, color: 'blue' },
  { value: 'BANK', label: 'Bank', icon: Building2, color: 'slate' }
];

// Quick Item Create Modal
const QuickItemModal = ({ isOpen, onClose, onSave, businessId }) => {
  const [formData, setFormData] = useState({
    name: '', item_type: 'PRODUCT', sale_price: '', gst_rate: 0, barcode: '', unit: 'PCS', opening_stock: 0
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.name || !formData.sale_price) {
      toast.error('Name and Price are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/pos/items/quick-create', { ...formData, sale_price: parseFloat(formData.sale_price) });
      toast.success('Item created');
      onSave(data.item);
      onClose();
      setFormData({ name: '', item_type: 'PRODUCT', sale_price: '', gst_rate: 0, barcode: '', unit: 'PCS', opening_stock: 0 });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error creating item'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-backdropFadeIn">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl animate-modalSlideIn">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-[15px] font-semibold text-slate-800">Quick Add Item</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">Item Name *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500" placeholder="Enter item name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] text-slate-500 mb-1">Sale Price *</label>
              <input type="number" value={formData.sale_price} onChange={(e) => setFormData(p => ({ ...p, sale_price: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-[12px] text-slate-500 mb-1">GST Rate (%)</label>
              <select value={formData.gst_rate} onChange={(e) => setFormData(p => ({ ...p, gst_rate: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500">
                <option value={0}>0%</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18%</option>
                <option value={28}>28%</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] text-slate-500 mb-1">Barcode</label>
              <input type="text" value={formData.barcode} onChange={(e) => setFormData(p => ({ ...p, barcode: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500" placeholder="Scan or type" />
            </div>
            <div>
              <label className="block text-[12px] text-slate-500 mb-1">Unit</label>
              <select value={formData.unit} onChange={(e) => setFormData(p => ({ ...p, unit: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500">
                <option value="PCS">PCS</option>
                <option value="KG">KG</option>
                <option value="L">L</option>
                <option value="M">M</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-[13px]">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-[13px] hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Creating...' : 'Create & Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hold Bills Dropdown
const HoldBillsDropdown = ({ holds, onRestore, onDelete }) => {
  const [open, setOpen] = useState(false);
  
  if (holds.length === 0) return null;
  
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 text-[12px] font-medium">
        <Pause className="w-4 h-4" />
        Held ({holds.length})
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 z-20">
          <div className="p-3 border-b border-slate-100">
            <p className="text-[12px] font-semibold text-slate-600">Held Bills</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {holds.map(hold => (
              <div key={hold.id} className="p-3 border-b border-slate-100 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-medium text-slate-800">{hold.name}</p>
                    <p className="text-[11px] text-slate-500">{hold.item_count} items • ₹{hold.grand_total?.toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { onRestore(hold); setOpen(false); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(hold.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// POS More Actions Dropdown - Consolidated sub-features
const POSMoreDropdown = ({ bid, navigate }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = [
    { path: '/pos/refunds', label: 'Refunds', icon: RotateCcw, color: 'text-orange-600' },
    { path: '/pos/cash-drawer', label: 'Cash Drawer', icon: DollarSign, color: 'text-emerald-600' },
    { path: '/pos/multi-screen', label: 'Multi-Counter', icon: Monitor, color: 'text-purple-600' },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setOpen(!open)} 
        className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[12px] font-medium transition-colors"
      >
        <MoreHorizontal className="w-3.5 h-3.5" /> More
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-30 overflow-hidden">
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              onClick={() => { navigate(`/business/${bid}${item.path}`); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-100 last:border-0"
            >
              <item.icon className={`w-4 h-4 ${item.color}`} />
              <span className="text-[13px] text-slate-700">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const POSBillingPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const searchInputRef = useRef(null);
  const amountReceivedRef = useRef(null);
  
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [customer, setCustomer] = useState({ name: 'Walk-in Customer', id: null, phone: null });
  const [customers, setCustomers] = useState([]);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [payments, setPayments] = useState([{ mode: 'CASH', amount: '', reference: '' }]);
  const [splitPayment, setSplitPayment] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [roundOff, setRoundOff] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [holds, setHolds] = useState([]);
  const [showQuickItem, setShowQuickItem] = useState(false);
  const [settings, setSettings] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  
  const bid = businessId || activeBusiness?.id;

  // Fetch data on mount
  useEffect(() => {
    if (bid) {
      fetchHolds();
      fetchCustomers();
      fetchSettings();
    }
    searchInputRef.current?.focus();
  }, [bid]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowResults(false);
        setShowCustomerSearch(false);
      }
      if (e.key === 'F6') { e.preventDefault(); handleSaveAndPrint(); }
      if (e.key === 'F7') { e.preventDefault(); handleSave(); }
      if (e.ctrlKey && e.key === 'b') { e.preventDefault(); handleHold(); }
      if (e.ctrlKey && e.key === 'i') { e.preventDefault(); setShowQuickItem(true); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart, customer, payments, discountAmount, additionalCharges, roundOff]);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/pos/settings');
      setSettings(data);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load POS settings')); }
  };

  const fetchHolds = async () => {
    try {
      const { data } = await api.get('/pos/holds');
      setHolds(data.holds || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load held bills')); }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/parties', { params: { type: 'customer' } });
      setCustomers(data.parties || data || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load customers')); }
  };

  // Debounced search
  const searchItems = useCallback(async (query) => {
    if (!query || query.length < 1) { setSearchResults([]); return; }
    try {
      const { data } = await api.get('/pos/items/search', { params: { q: query } });
      if (data.exact_match && data.items.length === 1) {
        addToCart(data.items[0]);
        setSearchQuery('');
        setSearchResults([]);
      } else {
        setSearchResults(data.items || []);
        setShowResults(true);
      }
    } catch (error) { toast.error(getApiErrorMessage(error, 'Error searching items')); }
  }, [bid]);

  useEffect(() => {
    const timer = setTimeout(() => { if (searchQuery) searchItems(searchQuery); }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, searchItems]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.id);
      if (existing) {
        return prev.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1, amount: (c.quantity + 1) * c.price } : c);
      }
      const price = item.sale_price || item.selling_price || 0;
      const taxRate = item.gst_rate || item.tax_rate || 0;
      return [...prev, {
        item_id: item.id,
        item_name: item.name,
        item_code: item.code,
        barcode: item.barcode,
        hsn_code: item.hsn_code,
        quantity: 1,
        unit: item.unit || 'Nos',
        price: price,
        discount: 0,
        discount_percent: 0,
        tax_rate: taxRate,
        tax_amount: price * (taxRate / 100),
        amount: price
      }];
    });
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    searchInputRef.current?.focus();
  };

  const updateCartItem = (index, field, value) => {
    setCart(prev => {
      const newCart = [...prev];
      newCart[index] = { ...newCart[index], [field]: value };
      // Recalculate
      const qty = newCart[index].quantity;
      const price = newCart[index].price;
      const discount = newCart[index].discount || 0;
      const taxRate = newCart[index].tax_rate || 0;
      const lineTotal = (qty * price) - discount;
      newCart[index].tax_amount = lineTotal * (taxRate / 100);
      newCart[index].amount = lineTotal + newCart[index].tax_amount;
      return newCart;
    });
  };

  const removeFromCart = (index) => setCart(prev => prev.filter((_, i) => i !== index));

  // Calculations
  const subtotal = cart.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const totalDiscount = discountAmount + cart.reduce((sum, item) => sum + (item.discount || 0), 0);
  const taxAmount = cart.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const preRoundTotal = subtotal - totalDiscount + taxAmount + additionalCharges;
  const roundOffAmount = roundOff ? (Math.round(preRoundTotal) - preRoundTotal) : 0;
  const grandTotal = preRoundTotal + roundOffAmount;
  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const changeDue = Math.max(0, totalPaid - grandTotal);
  const balanceDue = Math.max(0, grandTotal - totalPaid);

  const addPayment = () => {
    setPayments(prev => [...prev, { mode: 'UPI', amount: '', reference: '' }]);
  };

  const removePayment = (index) => {
    if (payments.length > 1) {
      setPayments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updatePayment = (index, field, value) => {
    setPayments(prev => {
      const newPayments = [...prev];
      newPayments[index] = { ...newPayments[index], [field]: value };
      return newPayments;
    });
  };

  const executeSave = async (print = false) => {
    setProcessing(true);
    try {
      const validPayments = payments.filter(p => parseFloat(p.amount) > 0).map(p => ({
        mode: p.mode,
        amount: parseFloat(p.amount),
        reference: p.reference || null
      }));
      if (validPayments.length === 0) {
        validPayments.push({ mode: 'CASH', amount: grandTotal, reference: null });
      }
      const payload = {
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        items: cart,
        subtotal,
        discount_amount: totalDiscount,
        tax_amount: taxAmount,
        additional_charges: additionalCharges,
        round_off: roundOffAmount,
        grand_total: grandTotal,
        payments: validPayments,
      };

      const { data } = await api.post('/pos/invoice', payload);
      toast.success(`Invoice ${data.invoice?.invoice_number} created`);
      if (print) printReceipt(data.invoice);
      clearCart();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create invoice'));
    } finally {
      setProcessing(false);
    }
  };

  const handleSave = (print = false) => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    if (totalPaid < grandTotal && totalPaid > 0) {
      setConfirmModal({
        title: 'Partial Payment',
        description: `Amount received (₹${totalPaid.toFixed(2)}) is less than total (₹${grandTotal.toFixed(2)}). Create partial payment?`,
        confirmLabel: 'Create Partial',
        confirmClass: 'bg-amber-600 hover:bg-amber-700',
        onConfirm: () => { setConfirmModal(null); executeSave(print); }
      });
      return;
    }
    executeSave(print);
  };

  const handleSaveAndPrint = () => handleSave(true);

  const printReceipt = (invoice) => {
    const changeDue = Math.max(0, (invoice.total_paid || 0) - (invoice.grand_total || 0));
    const receiptWindow = window.open('', '_blank', 'width=300,height=600');
    receiptWindow.document.write(`
      <html><head><title>Receipt</title>
      <style>
        body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 10px; }
        .center { text-align: center; }
        .right { text-align: right; }
        .bold { font-weight: bold; }
        .line { border-top: 1px dashed #000; margin: 8px 0; }
        .row { display: flex; justify-content: space-between; margin: 2px 0; }
        h2 { margin: 0; font-size: 14px; }
      </style></head><body>
      <div class="center bold"><h2>RECEIPT</h2></div>
      <div class="center">${invoice.invoice_number}</div>
      <div class="center">${new Date().toLocaleString()}</div>
      <div class="line"></div>
      <div class="bold">Customer: ${invoice.customer_name}</div>
      <div class="line"></div>
      ${invoice.items.map(item => `
        <div>${item.item_name}</div>
        <div class="row"><span>${item.quantity} x ₹${item.price}</span><span>₹${item.amount?.toFixed(2)}</span></div>
      `).join('')}
      <div class="line"></div>
      <div class="row"><span>Subtotal</span><span>₹${invoice.subtotal?.toFixed(2)}</span></div>
      ${invoice.discount_amount > 0 ? `<div class="row"><span>Discount</span><span>-₹${invoice.discount_amount?.toFixed(2)}</span></div>` : ''}
      ${invoice.tax_amount > 0 ? `<div class="row"><span>Tax</span><span>₹${invoice.tax_amount?.toFixed(2)}</span></div>` : ''}
      ${invoice.round_off !== 0 ? `<div class="row"><span>Round Off</span><span>₹${invoice.round_off?.toFixed(2)}</span></div>` : ''}
      <div class="line"></div>
      <div class="row bold"><span>TOTAL</span><span>₹${invoice.grand_total?.toFixed(2)}</span></div>
      <div class="row"><span>Paid (${invoice.payments?.[0]?.mode})</span><span>₹${invoice.total_paid?.toFixed(2)}</span></div>
      ${changeDue > 0 ? `<div class="row"><span>Change</span><span>₹${changeDue.toFixed(2)}</span></div>` : ''}
      <div class="line"></div>
      <div class="center">Thank you for your purchase!</div>
      </body></html>
    `);
    receiptWindow.document.close();
    receiptWindow.print();
  };

  const handleHold = async () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    try {
      const payload = {
        name: `Hold - ${customer.name}`,
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        items: cart,
        subtotal,
        discount_amount: totalDiscount,
        additional_charges: additionalCharges,
        tax_amount: taxAmount,
        grand_total: grandTotal
      };
      await api.post('/pos/hold', payload);
      toast.success('Bill held');
      clearCart();
      fetchHolds();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error holding bill'));
    }
  };

  const restoreHold = async (hold) => {
    setCart(hold.items || []);
    setCustomer({ name: hold.customer_name || hold.name, id: hold.customer_id || hold.party_id, phone: hold.customer_phone });
    setDiscountAmount(hold.discount_amount || 0);
    setAdditionalCharges(hold.additional_charges || 0);

    try {
      await api.delete(`/pos/holds/${hold.id}`);
      fetchHolds();
    } catch (error) { toast.error(getApiErrorMessage(error, 'Error removing hold')); }
  };

  const deleteHold = async (holdId) => {
    try {
      await api.delete(`/pos/holds/${holdId}`);
      fetchHolds();
      toast.success('Hold deleted');
    } catch (error) { toast.error(getApiErrorMessage(error, 'Error deleting hold')); }
  };

  const clearCart = () => {
    setCart([]);
    setCustomer({ name: 'Walk-in Customer', id: null, phone: null });
    setPayments([{ mode: 'CASH', amount: '', reference: '' }]);
    setSplitPayment(false);
    setDiscountAmount(0);
    setAdditionalCharges(0);
    searchInputRef.current?.focus();
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch)
  );

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden">
      {/* TOP BAR */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/business/${bid}/dashboard`)} className="flex items-center gap-2 text-slate-600 hover:text-slate-800 text-[13px]">
              <ArrowLeft className="w-4 h-4" /> Exit POS
            </button>
            <div className="h-5 w-px bg-slate-200"></div>
            <h1 className="text-[16px] font-semibold text-slate-800">POS / Quick Sale</h1>
          </div>
          <div className="flex items-center gap-2">
            <HoldBillsDropdown holds={holds} onRestore={restoreHold} onDelete={deleteHold} />
            {/* Quick Action Buttons */}
            <button onClick={() => navigate(`/business/${bid}/pos/bills`)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[12px] font-medium transition-colors">
              <Receipt className="w-3.5 h-3.5" /> Bills
            </button>
            <button onClick={() => navigate(`/business/${bid}/pos/daily-report`)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[12px] font-medium transition-colors">
              <BarChart3 className="w-3.5 h-3.5" /> Report
            </button>
            {/* More Dropdown */}
            <POSMoreDropdown bid={bid} navigate={navigate} />
            <button onClick={() => navigate(`/business/${bid}/pos/settings`)} className="p-2 hover:bg-slate-100 rounded-lg" title="POS Settings">
              <Settings className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT PANEL - CART */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden" style={{ width: '65%' }}>
          {/* Search Bar */}
          <div className="flex-shrink-0 mb-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                className="w-full pl-12 pr-4 py-3.5 bg-white rounded-xl text-[14px] shadow-sm border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
                placeholder="Search item name / code / scan barcode..."
                autoFocus
              />
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 max-h-72 overflow-y-auto">
                  {searchResults.map(item => (
                    <div
                      key={item.id}
                      onClick={() => addToCart(item)}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[13px] font-medium text-slate-800">{item.name}</p>
                          <p className="text-[11px] text-slate-500">{item.code} {item.barcode && `• ${item.barcode}`}</p>
                        </div>
                        <p className="text-[14px] font-semibold text-slate-800">₹{item.sale_price || item.selling_price || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Quick Actions */}
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => setShowQuickItem(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-600 hover:bg-slate-50">
                <Plus className="w-3.5 h-3.5" /> New Item (Ctrl+I)
              </button>
              <div className="text-[10px] text-slate-400 ml-auto">F6: Save & Print • F7: Save • Ctrl+B: Hold</div>
            </div>
          </div>

          {/* Cart Table */}
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
            <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-3 text-[11px] font-semibold text-slate-500 uppercase">
                <span className="w-8">#</span>
                <span className="flex-1">Item</span>
                <span className="w-20 text-center">Qty</span>
                <span className="w-24 text-right">Price</span>
                <span className="w-24 text-right">Amount</span>
                <span className="w-10"></span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <ShoppingCart className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-[13px]">Cart is empty</p>
                  <p className="text-[11px] mt-1">Search or scan items to add</p>
                </div>
              ) : (
                cart.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50">
                    <span className="w-8 text-[12px] text-slate-400">{index + 1}</span>
                    <div className="flex-1">
                      <p className="text-[13px] font-medium text-slate-800">{item.item_name}</p>
                      <p className="text-[10px] text-slate-400">{item.item_code}</p>
                    </div>
                    <div className="w-20 flex items-center justify-center gap-1">
                      <button onClick={() => updateCartItem(index, 'quantity', Math.max(1, item.quantity - 1))} className="p-1 hover:bg-slate-200 rounded">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateCartItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-10 text-center text-[13px] border border-slate-200 rounded py-1 focus:outline-none focus:border-blue-500"
                        min="1"
                      />
                      <button onClick={() => updateCartItem(index, 'quantity', item.quantity + 1)} className="p-1 hover:bg-slate-200 rounded">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) => updateCartItem(index, 'price', parseFloat(e.target.value) || 0)}
                      className="w-24 text-right text-[13px] border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-blue-500"
                    />
                    <span className="w-24 text-right text-[13px] font-medium text-slate-800">₹{item.amount?.toFixed(2)}</span>
                    <button onClick={() => removeFromCart(index)} className="w-10 flex justify-center p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - SUMMARY & PAYMENT */}
        <div className="w-[35%] bg-white border-l border-slate-200 flex flex-col p-4 overflow-y-auto">
          {/* Customer Selection */}
          <div className="mb-4">
            <label className="block text-[11px] text-slate-500 uppercase font-medium mb-2">Customer</label>
            <div className="relative">
              <button
                onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-200 text-left hover:bg-slate-100"
              >
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-[13px] text-slate-700 flex-1">{customer.name}</span>
              </button>
              {showCustomerSearch && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200">
                  <div className="p-2">
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 rounded-lg text-[13px] focus:outline-none"
                      placeholder="Search customer..."
                      autoFocus
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <div
                      onClick={() => { setCustomer({ name: 'Walk-in Customer', id: null, phone: null }); setShowCustomerSearch(false); }}
                      className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-[13px]"
                    >
                      Walk-in Customer
                    </div>
                    {filteredCustomers.slice(0, 6).map(c => (
                      <div
                        key={c.party_id || c.id}
                        onClick={() => { setCustomer({ name: c.name, id: c.party_id || c.id, phone: c.mobile }); setShowCustomerSearch(false); }}
                        className="px-4 py-2 hover:bg-slate-50 cursor-pointer"
                      >
                        <p className="text-[13px] text-slate-800">{c.name}</p>
                        <p className="text-[11px] text-slate-500">{c.mobile}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-xl p-4 mb-4">
            <div className="space-y-2.5">
              <div className="flex justify-between text-[13px]">
                <span className="text-slate-600">Subtotal ({cart.length} items)</span>
                <span className="text-slate-800">₹{subtotal.toFixed(2)}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-slate-600">Discount</span>
                  <span className="text-red-600">-₹{totalDiscount.toFixed(2)}</span>
                </div>
              )}
              {taxAmount > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-slate-600">Tax</span>
                  <span className="text-slate-800">₹{taxAmount.toFixed(2)}</span>
                </div>
              )}
              {additionalCharges > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-slate-600">Additional Charges</span>
                  <span className="text-slate-800">₹{additionalCharges.toFixed(2)}</span>
                </div>
              )}
              {roundOff && roundOffAmount !== 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-slate-600">Round Off</span>
                  <span className="text-slate-800">{roundOffAmount >= 0 ? '+' : ''}₹{roundOffAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-slate-200 pt-2.5 mt-2.5">
                <div className="flex justify-between">
                  <span className="text-[15px] font-semibold text-slate-800">Grand Total</span>
                  <span className="text-[18px] font-bold text-blue-600">₹{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Discount & Charges */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">Discount</label>
              <input
                type="number"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">Add. Charges</label>
              <input
                type="number"
                value={additionalCharges}
                onChange={(e) => setAdditionalCharges(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                placeholder="0"
              />
            </div>
          </div>

          {/* Round Off Toggle */}
          <div className="flex items-center gap-2 mb-4">
            <input type="checkbox" id="roundOff" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)} className="rounded" />
            <label htmlFor="roundOff" className="text-[12px] text-slate-600">Auto Round Off</label>
          </div>

          {/* Payment Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] text-slate-500 uppercase font-medium">Payment</label>
              <button
                onClick={() => { setSplitPayment(!splitPayment); if (!splitPayment) addPayment(); }}
                className={`text-[11px] font-medium ${splitPayment ? 'text-blue-600' : 'text-slate-500 hover:text-blue-600'}`}
              >
                {splitPayment ? '✓ Split Payment' : '+ Split Payment'}
              </button>
            </div>
            
            {payments.map((payment, index) => (
              <div key={index} className={`${index > 0 ? 'mt-3 pt-3 border-t border-slate-200' : ''}`}>
                {splitPayment && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-slate-500">Payment {index + 1}</span>
                    {index > 0 && (
                      <button onClick={() => removePayment(index)} className="text-[10px] text-red-500 hover:text-red-700">Remove</button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  {PAYMENT_MODES.map(mode => (
                    <button
                      key={mode.value}
                      onClick={() => updatePayment(index, 'mode', mode.value)}
                      className={`flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-lg border-2 transition-all ${
                        payment.mode === mode.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <mode.icon className={`w-3.5 h-3.5 ${payment.mode === mode.value ? 'text-blue-600' : 'text-slate-400'}`} />
                      <span className={`text-[9px] font-medium ${payment.mode === mode.value ? 'text-blue-700' : 'text-slate-500'}`}>{mode.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={payment.amount}
                    onChange={(e) => updatePayment(index, 'amount', e.target.value)}
                    className="flex-1 px-3 py-2.5 border-2 border-slate-200 rounded-lg text-[14px] font-semibold text-center focus:outline-none focus:border-blue-500"
                    placeholder={index === 0 ? grandTotal.toFixed(2) : '0.00'}
                  />
                  {(payment.mode === 'UPI' || payment.mode === 'CARD') && (
                    <input
                      type="text"
                      value={payment.reference}
                      onChange={(e) => updatePayment(index, 'reference', e.target.value)}
                      className="w-24 px-2 py-2.5 border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:border-blue-500"
                      placeholder="Ref #"
                    />
                  )}
                </div>
              </div>
            ))}
            
            {splitPayment && (
              <button onClick={addPayment} className="mt-2 w-full py-2 text-[12px] text-blue-600 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300">
                + Add Another Payment
              </button>
            )}
            
            {/* Payment Summary */}
            <div className="mt-3 p-2.5 bg-slate-100 rounded-lg">
              <div className="flex justify-between text-[12px]">
                <span className="text-slate-600">Total Paid</span>
                <span className={`font-semibold ${totalPaid >= grandTotal ? 'text-emerald-600' : 'text-slate-800'}`}>₹{totalPaid.toFixed(2)}</span>
              </div>
              {changeDue > 0 && (
                <div className="flex justify-between text-[12px] mt-1">
                  <span className="text-emerald-600">Change Due</span>
                  <span className="font-bold text-emerald-700">₹{changeDue.toFixed(2)}</span>
                </div>
              )}
              {balanceDue > 0 && totalPaid > 0 && (
                <div className="flex justify-between text-[12px] mt-1">
                  <span className="text-amber-600">Balance Due</span>
                  <span className="font-bold text-amber-700">₹{balanceDue.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-auto space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleHold}
                disabled={cart.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl font-medium text-[13px] hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Pause className="w-4 h-4" /> Hold
              </button>
              <button
                onClick={clearCart}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium text-[13px] hover:bg-slate-200"
              >
                <X className="w-4 h-4" /> Clear
              </button>
            </div>
            <button
              onClick={() => handleSave(false)}
              disabled={cart.length === 0 || processing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-[14px] hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" /> {processing ? 'Processing...' : 'Save Bill (F7)'}
            </button>
            <button
              onClick={handleSaveAndPrint}
              disabled={cart.length === 0 || processing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-emerald-600 text-white rounded-xl font-semibold text-[14px] hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="w-5 h-5" /> Save & Print (F6)
            </button>
          </div>
        </div>
      </div>

      {/* Quick Item Modal */}
      <QuickItemModal isOpen={showQuickItem} onClose={() => setShowQuickItem(false)} onSave={addToCart} businessId={bid} />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default POSBillingPage;
